import React from "react";
import { Box, Button, Paper, Typography, styled } from "@mui/material";
import { Link } from "react-router-dom";
import { useConsent } from "../../hooks/useConsent";
import { CONSENT_TEXTS } from "../../pages/shared/constants";

/**
 * The consent prompt (EB-559).
 *
 * Anchored to the bottom rather than centred in a modal: a full-screen overlay on a phone is the
 * pattern that makes shoppers click whatever dismisses it fastest, and consent given to get rid of
 * a wall is not a decision. This leaves the catalog visible and usable behind it.
 *
 * **Both buttons carry the same visual weight.** "Aceptar" as a filled primary next to a rejection
 * dressed as a text link is the standard dark pattern, and it is the one thing that would make the
 * whole feature decorative — an unfree choice is not consent, so the two are the same size, the
 * same variant, and adjacent.
 */

const Bar = styled(Paper)(({ theme }) => ({
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  // Above the page but below MUI's modal layer, so a dialog opened from the footer link is not
  // covered by the banner that sent the shopper there.
  zIndex: theme.zIndex.drawer,
  padding: theme.spacing(2),
  borderRadius: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 4),
  },
}));

const Actions = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  flexShrink: 0,
  "& > *": {
    // Equal width on phones, where the two would otherwise size themselves to their labels and the
    // longer one would read as the intended answer.
    flex: 1,
  },
  [theme.breakpoints.up("md")]: {
    "& > *": { flex: "initial" },
  },
}));

const ChoiceButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 700,
  borderRadius: 999,
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  whiteSpace: "nowrap",
}));

const CookieBanner: React.FC = () => {
  const { needsDecision, decide } = useConsent();

  if (!needsDecision) return null;

  return (
    <Bar elevation={8} role="dialog" aria-live="polite" aria-label={CONSENT_TEXTS.bannerTitle}>
      <Box>
        <Typography sx={{ fontWeight: 700, mb: 0.5 }}>{CONSENT_TEXTS.bannerTitle}</Typography>
        <Typography variant="body2" color="text.secondary">
          {CONSENT_TEXTS.bannerBody}{" "}
          <Link to="/politica-de-privacidad#cookies">{CONSENT_TEXTS.policyLink}</Link>
        </Typography>
      </Box>

      <Actions>
        <ChoiceButton variant="outlined" color="inherit" onClick={() => decide("denied")}>
          {CONSENT_TEXTS.reject}
        </ChoiceButton>
        <ChoiceButton variant="outlined" color="inherit" onClick={() => decide("granted")}>
          {CONSENT_TEXTS.accept}
        </ChoiceButton>
      </Actions>
    </Bar>
  );
};

export default CookieBanner;
